import axios from "axios";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Flex,
  ButtonLink,
  Divider,
  Text,
  Accordion,
  Status,
  TextField,
  TextArea,
  Button,
  IconButton,
} from "gestalt";
import React, { useState, useEffect, useCallback } from "react";
import { Radar } from "../components/Feedback/Radar";
import { Header } from "../components/Header";
import { useUser } from "@clerk/clerk-react";
import { getUser } from "../components/getUser";

const Feedback = () => {
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const { isSignedIn, user, isLoaded } = useUser();
  const [feedback, setFeedback] = useState({
    candidate: "",
    overview: "",
    content: [],
    chatHistory: [],
    originalOverview: "",
    originalContent: [],
    databaseFeedbackID: "",
  });
  const [interviewRound, setInterviewRound] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [istotalScoreModified, setIstotalScoreModified] = useState(false);
  const params = useParams();
  const navigate = useNavigate();

  const rootUrl = process.env.REACT_APP_BACKEND_URL;
  const query = new URLSearchParams(useLocation().search);
  const jobId = query.get("jobId");
  const candidateName = query.get("name");

  const [expandedSections, setExpandedSections] = useState({});
  const [expandedSectionsBackup, setExpandedSectionsBackup] = useState({});
  const [expandedOverview, setExpandedOverview] = useState({});
  const [expandedOverallScore, setExpandedOverallScore] = useState({});

  const scoreDelimiter = 3;

  const handleDoubleClick = (id) => {
    if (!expandedSections[id]) {
      console.error(`Invalid type: ${id}`);
      return;
    }
    setExpandedSections((prevSections) => ({
      ...prevSections,
      [id]: {
        ...prevSections[id],
        isExpanded: !prevSections[id]?.isExpanded,
      },
    }));
  };

  const handleOverviewDoubleClick = () => {
    setExpandedOverview((prev) => ({
      ...prev,
      isExpanded: !prev.isExpanded,
    }));
  };

  const handleOverallScoreDoubleClick = () => {
    setExpandedSectionsBackup(expandedSections);
    setExpandedOverallScore((prev) => ({
      ...prev,
      isExpanded: true,
    }));
  };

  const handleCancelAllSections = () => {
    setExpandedSections(expandedSectionsBackup);
    setExpandedOverallScore((prev) => ({
      ...prev,
      isExpanded: false,
      overallScore:
        feedback.content && feedback.content.length > 0
          ? feedback.content.reduce((acc, item) => acc + (item.score || 0), 0) /
            feedback.content.length
          : 0,
    }));
  };

  const handleSectionChange = (itemType, value) => {
    let score = Number(value);
    if (isNaN(score)) score = 0;
    if (score < 0) score = 0;
    if (score > 5) score = 5;
    setExpandedSections((prevSections) => ({
      ...prevSections,
      [itemType]: {
        ...prevSections[itemType],
        score: score,
      },
    }));
  };

  const defaultRadarData = [
    {
      section: "Default",
      "score (0-5)": 0,
    },
  ];

  const [radarData, setRadarData] = useState(defaultRadarData);

  const fetchFeedbackData = useCallback(
    async (id) => {
      if (isLoaded) {
        try {
          let currentUser = {
            emailAddress: user.primaryEmailAddress.emailAddress,
            firstName: user.firstName,
            lastName: user.lastName,
          };
          let userInfo = await getUser(isSignedIn, currentUser);
          const feedbackResult = await axios.post(
            rootUrl + "/api/bData/applicantFeedback",
            { recruiterID: userInfo.id, feedbackId: id, jobId: jobId }
          );

          setFeedback({
            candidateName: candidateName,
            overview: feedbackResult.data.editedFeedback.editedContent,
            content: feedbackResult.data.editedFeedback.dimensions,
            originalOverview:
              feedbackResult.data.originalFeedback.editedContent,
            originalContent: feedbackResult.data.originalFeedback.dimensions,
            chatHistory: feedbackResult.data.chatHistory,
            userEmail: feedbackResult.data.userEmail,
            databaseFeedbackID: feedbackResult.data.editedFeedback.ID,
          });

          setInterviewRound(feedbackResult.data.interviewRound || "");

          setExpandedOverview({
            isExpanded: false,
            content: feedbackResult.data.editedFeedback.editedContent,
          });

          const initialExpandedSections =
            feedbackResult.data.editedFeedback.dimensions.reduce(
              (acc, section) => ({
                ...acc,
                [section.type]: {
                  isExpanded: false,
                  comment: section.comment,
                  score: section.score,
                },
              }),
              {}
            );

          setExpandedSections(initialExpandedSections);

          setIstotalScoreModified(
            feedbackResult.data.editedFeedback.isModified
          );

          const isOverallScoreModified =
            feedbackResult.data.editedFeedback.isModified;
          const overallScore = isOverallScoreModified
            ? getOverallScore(feedbackResult.data.editedFeedback.dimensions)
            : getOverallScore(feedbackResult.data.originalFeedback.dimensions);

          setExpandedOverallScore({
            isExpanded: false,
            overallScore: overallScore,
          });

          setIsAuthorized(true);
        } catch (error) {
          if (error.response && error.response.status === 403) {
            setIsAuthorized(false);
            alert("Unauthorized data access!");
          }
        }
      }
    },
    [isLoaded, user, isSignedIn, rootUrl, candidateName, jobId]
  );

  const getOverallScore = (content) => {
    if (!content || content.length === 0) return 0;
    const totalScore = content.reduce(
      (acc, item) => acc + (item.score || 0),
      0
    );
    return totalScore / content.length;
  };

  useEffect(() => {
    if (params.id === undefined) {
      setErrorMsg("No Valid Feedback ID");
    } else {
      fetchFeedbackData(params.id);
    }
  }, [params.id, fetchFeedbackData]);

  useEffect(() => {
    const newRadarData = Object.keys(expandedSections).map((type) => {
      let score = Number(expandedSections[type]?.score);
      if (isNaN(score)) score = 0;
      return {
        section: type,
        "score (0-5)": score,
      };
    });
    setRadarData(newRadarData);
  }, [expandedSections, feedback.content]);

  const topSkills =
    feedback.content?.filter((item) => item.score > scoreDelimiter) || [];
  const skillsToImprove =
    feedback.content?.filter((item) => item.score <= scoreDelimiter) || [];
  if (!feedback) {
    return <div>{"Loading..."}</div>;
  }

  if (errorMsg !== "") {
    return (
      <>
        <Text align="left" size="300" weight="bold" color="red">
          {errorMsg}
        </Text>
      </>
    );
  }

  if (!isAuthorized) {
    return null;
  }

  const saveAllEditedContent = async () => {
    setIsSaving(true);
    setIsSaved(false);
    const updatedDimensions = feedback.content.map((item) => ({
      type: item.type,
      score:
        typeof expandedSections[item.type]?.score === "number" &&
        !isNaN(expandedSections[item.type]?.score)
          ? expandedSections[item.type]?.score
          : item.score,
      comment: expandedSections[item.type]?.comment || item.comment,
    }));

    const editedFeedback = {
      ID: feedback.databaseFeedbackID,
      editedContent: expandedOverview.content,
      overallScore:
        typeof expandedOverallScore.overallScore === "number" &&
        !isNaN(expandedOverallScore.overallScore)
          ? expandedOverallScore.overallScore
          : 0,
      dimensions: updatedDimensions,
      isModified: true,
    };

    try {
      await axios.post(`${rootUrl}/api/bData/updateFeedback`, editedFeedback);
      setIsSaving(false);
      setIsSaved(true);
      await fetchFeedbackData(feedback.databaseFeedbackID);
      setTimeout(() => {
        setIsSaved(false);
        setExpandedOverallScore((prev) => ({
          ...prev,
          isExpanded: false,
        }));
      }, 2000);
    } catch (error) {
      setIsSaving(false);
      setIsSaved(false);
      console.error("Error saving changes:", error);
      alert("Failed to save changes.");
    }
  };

  const revertToOriginalFeedback = async () => {
    try {
      let curUser = {
        emailAddress: user.primaryEmailAddress.emailAddress,
        firstName: user.firstName,
        lastName: user.lastName,
      };
      let userInfo = await getUser(isSignedIn, curUser);
      const response = await axios.post(
        `${rootUrl}/api/bData/revertToOriginalFeedback`,
        {
          recruiterId: userInfo.id,
          feedbackId: feedback.databaseFeedbackID,
        }
      );

      if (response.data.status === "success") {
        alert("Feedback reverted to original successfully");
        await fetchFeedbackData(feedback.databaseFeedbackID);
      } else {
        console.error(
          `failed to revert back to original feedback successfully for feedback id: ${feedback.databaseFeedbackID}`
        );
      }
    } catch (error) {
      console.error("Error reverting feedback:", error);
      alert("Failed to revert feedback.");
    }
  };

  return (
    <Box
      alignItems="center"
      height="100%"
      width="100%"
      justifyContent="center"
      padding={8}
      marginTop={6}
    >
      <Box marginTop={0} rounding={4} padding={4}>
        <Flex justifyContent="end" alignItems="center" gap={2}>
          <ButtonLink
            text="Back to List"
            size="md"
            onClick={() => navigate(`/candidateList/${query.get("jobId")}`)}
          />
        </Flex>
        <Flex justifyContent="start" alignItems="center" gap={2}>
          <img
            src="/icons/logo.png"
            width="40"
            height="40"
            alt="STK Logo"
            title="STK"
          />
          <Text align="left" size="600" weight="bold">
            Feedback for {feedback.candidateName}{" "}
            <span style={{ fontSize: "0.6em", fontWeight: "normal" }}>
              {feedback.userEmail}
            </span>
          </Text>
        </Flex>
        <div onDoubleClick={handleOverviewDoubleClick}>
          <Box
            marginTop={2}
            rounding={4}
            padding={2}
            dangerouslySetInlineStyle={{ __style: { cursor: "pointer" } }}
          >
            <Flex justifyContent="between" alignItems="center">
              {expandedOverview.isExpanded ? (
                <>
                  <Box width="100%" display="flex" direction="column" gap={2}>
                    <div
                      style={{
                        transform: "scale(0.8)",
                        transformOrigin: "top left",
                      }}
                    >
                      <TextArea
                        id="overview-edit"
                        onChange={({ value }) =>
                          setExpandedOverview((prev) => ({
                            ...prev,
                            content: value,
                          }))
                        }
                        value={expandedOverview.content || ""}
                        placeholder="Edit overview"
                        size="md"
                        autoComplete="off"
                        rows={3}
                      />

                      <Button
                        text="Save Overview"
                        color="blue"
                        size="md"
                        onClick={saveAllEditedContent}
                      />
                    </div>

                    <Text size="100" color="subtle">
                      {feedback.originalOverview}
                    </Text>
                  </Box>
                </>
              ) : (
                <Text size="100" color="subtle">
                  {expandedOverview.content}
                </Text>
              )}
            </Flex>
          </Box>
        </div>
      </Box>
      <Divider />

      <Box
        padding={8}
        height="100%"
        alignItems="center"
        justifyContent="center"
      >
        <Flex
          alignItems="stretch"
          justifyContent="between"
          width="100%"
          gap={2}
        >
          <Box marginTop={0} padding={3} height={550}>
            <Box borderStyle="shadow" marginTop={0} rounding={4} padding={6}>
              <Box marginTop={0} rounding={4} padding={0}>
                <Text align="left" size="300" weight="bold">
                  Overview Chart for {query.get("name")}'s interview
                </Text>
              </Box>
              {(() => {
                const cleanedRadarData = radarData.map((d) => ({
                  ...d,
                  "score (0-5)":
                    typeof d["score (0-5)"] === "number" &&
                    !isNaN(d["score (0-5)"])
                      ? d["score (0-5)"]
                      : 0,
                }));
                const hasNonZero = cleanedRadarData.some(
                  (d) => d["score (0-5)"] > 0
                );
                return hasNonZero ? (
                  <Radar radarData={cleanedRadarData} />
                ) : (
                  <Text color="subtle" size="300">
                    No scores to display
                  </Text>
                );
              })()}
            </Box>
          </Box>
          <Box marginTop={0} padding={3} flex="1" display="flex" width="100%">
            <Accordion
              id="ModuleExample - default - 3"
              title={
                <Flex alignItems="center" justifyContent="between" width="100%">
                  <Box>
                    <Text weight="bold" size="400">
                      {interviewRound.toUpperCase()} Evaluation
                    </Text>
                  </Box>
                  <div
                    style={{
                      width: "200px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Text
                      weight="bold"
                      size="400"
                      dangerouslySetInlineStyle={{
                        __style: { cursor: "pointer" },
                      }}
                    >
                      {`(${istotalScoreModified ? "Edited score" : "Score"}: ${
                        Number(expandedOverallScore.overallScore) === 0
                          ? 0
                          : Number(expandedOverallScore.overallScore).toFixed(1)
                      })`}
                    </Text>
                  </div>

                  <Box marginLeft="auto">
                    <IconButton
                      accessibilityLabel="Edit"
                      icon="edit"
                      iconColor="gray"
                      size="md"
                      onClick={handleOverallScoreDoubleClick}
                    />
                  </Box>
                </Flex>
              }
            >
              {topSkills.length > 0 && (
                <>
                  <Text color="subtle"> Candidate's top skills </Text>
                  {topSkills.map((item) => (
                    <div
                      onDoubleClick={() => handleDoubleClick(item.type)}
                      key={item.type}
                    >
                      <Box
                        padding={3}
                        height="100%"
                        display="flex"
                        alignItems="center"
                        justifyContent="start"
                        key={"improve-" + item.type}
                        dangerouslySetInlineStyle={{
                          __style: { cursor: "pointer" },
                        }}
                      >
                        {expandedOverallScore.isExpanded ? (
                          <>
                            <Box display="flex" direction="row" width="100%">
                              <Box
                                display="flex"
                                direction="column"
                                width="60%"
                                padding={3}
                              >
                                <Box
                                  display="flex"
                                  direction="row"
                                  alignItems="center"
                                  marginBottom={2}
                                >
                                  <Text weight="bold" size="300">
                                    {item.type}
                                  </Text>

                                  <Box width={80} marginStart={2}>
                                    <TextField
                                      id={`score-edit-${item.type}`}
                                      onChange={({ value }) => {
                                        let score = Number(value);
                                        if (isNaN(score)) score = 0;
                                        if (score < 0) score = 0;
                                        if (score > 5) score = 5;
                                        setExpandedSections((prevSections) => ({
                                          ...prevSections,
                                          [item.type]: {
                                            ...prevSections[item.type],
                                            score: score,
                                          },
                                        }));
                                      }}
                                      value={
                                        typeof expandedSections[item.type]
                                          ?.score === "number" &&
                                        !isNaN(
                                          expandedSections[item.type]?.score
                                        )
                                          ? expandedSections[item.type]?.score
                                          : 0
                                      }
                                      placeholder="Edit score"
                                      autoComplete="off"
                                      size="md"
                                    />
                                  </Box>
                                </Box>

                                <Box
                                  display="flex"
                                  direction="column"
                                  width="100%"
                                >
                                  <Text weight="bold" size="300">
                                    Comment:
                                  </Text>
                                  <Box marginTop={1}>
                                    <TextArea
                                      id={`comment-edit-${item.type}`}
                                      onChange={({ value }) =>
                                        setExpandedSections((prevSections) => ({
                                          ...prevSections,
                                          [item.type]: {
                                            ...prevSections[item.type],
                                            comment: value,
                                          },
                                        }))
                                      }
                                      value={
                                        expandedSections[item.type]?.comment ||
                                        ""
                                      }
                                      placeholder="Edit comment"
                                      autoComplete="off"
                                      size="md"
                                      rows={3}
                                    />
                                  </Box>
                                </Box>
                              </Box>
                              <Box padding={3} marginTop={2} width="40%">
                                <Status
                                  type="unstarted"
                                  title={`${item.type} (Score: ${
                                    feedback.originalContent.find(
                                      (originalItem) =>
                                        originalItem.type === item.type
                                    )?.score || 0
                                  })`}
                                  subtext={
                                    feedback.originalContent.find(
                                      (originalItem) =>
                                        originalItem.type === item.type
                                    )?.comment || "No comment available"
                                  }
                                />
                              </Box>
                            </Box>
                          </>
                        ) : (
                          <Status
                            type="ok"
                            title={`${item.type} (Score: ${
                              typeof expandedSections[item.type]?.score ===
                                "number" &&
                              !isNaN(expandedSections[item.type]?.score)
                                ? expandedSections[item.type]?.score
                                : 0
                            })`}
                            subtext={expandedSections[item.type]?.comment || ""}
                          />
                        )}
                      </Box>
                    </div>
                  ))}
                </>
              )}
              {skillsToImprove.length > 0 && (
                <>
                  <Text color="subtle"> Skills need to be improved </Text>
                  {skillsToImprove.map((item) => {
                    return (
                      <div key={item.type}>
                        <Box
                          padding={3}
                          height="100%"
                          display="flex"
                          alignItems="center"
                          justifyContent="start"
                          key={"improve-" + item.type}
                          dangerouslySetInlineStyle={{
                            __style: { cursor: "pointer" },
                          }}
                        >
                          {expandedOverallScore.isExpanded ? (
                            <>
                              <Box display="flex" direction="row" width="100%">
                                <Box
                                  display="flex"
                                  direction="column"
                                  width="60%"
                                  padding={3}
                                >
                                  <Box
                                    display="flex"
                                    direction="row"
                                    alignItems="center"
                                    marginBottom={2}
                                  >
                                    <Text weight="bold" size="300">
                                      {item.type}
                                    </Text>

                                    <Box width={80} marginStart={2}>
                                      <TextField
                                        id={`score-edit-${item.type}`}
                                        onChange={({ value }) =>
                                          handleSectionChange(item.type, value)
                                        }
                                        value={
                                          typeof expandedSections[item.type]
                                            ?.score === "number" &&
                                          !isNaN(
                                            expandedSections[item.type]?.score
                                          )
                                            ? expandedSections[item.type]?.score
                                            : 0
                                        }
                                        placeholder="Edit score"
                                        autoComplete="off"
                                        size="md"
                                      />
                                    </Box>
                                  </Box>

                                  <Box
                                    display="flex"
                                    direction="column"
                                    width="100%"
                                  >
                                    <Text weight="bold" size="300">
                                      Comment:
                                    </Text>
                                    <Box marginTop={1}>
                                      <TextArea
                                        id={`comment-edit-${item.type}`}
                                        onChange={({ value }) =>
                                          setExpandedSections(
                                            (prevSections) => ({
                                              ...prevSections,
                                              [item.type]: {
                                                ...prevSections[item.type],
                                                comment: value,
                                              },
                                            })
                                          )
                                        }
                                        value={
                                          expandedSections[item.type]
                                            ?.comment || ""
                                        }
                                        placeholder="Edit comment"
                                        autoComplete="off"
                                        size="md"
                                        rows={3}
                                      />
                                    </Box>
                                  </Box>
                                </Box>

                                <Box padding={3} marginTop={2} width="40%">
                                  <Status
                                    type="unstarted"
                                    title={`${item.type} (Score: ${
                                      feedback.originalContent.find(
                                        (originalItem) =>
                                          originalItem.type === item.type
                                      )?.score || 0
                                    })`}
                                    subtext={
                                      feedback.originalContent.find(
                                        (originalItem) =>
                                          originalItem.type === item.type
                                      )?.comment || "No comment available"
                                    }
                                  />
                                </Box>
                              </Box>
                            </>
                          ) : (
                            <Status
                              type="problem"
                              title={`${item.type} (Score: ${
                                typeof expandedSections[item.type]?.score ===
                                  "number" &&
                                !isNaN(expandedSections[item.type]?.score)
                                  ? expandedSections[item.type]?.score
                                  : 0
                              })`}
                              subtext={
                                expandedSections[item.type]?.comment || ""
                              }
                            />
                          )}
                        </Box>
                      </div>
                    );
                  })}
                </>
              )}
              {expandedOverallScore.isExpanded && (
                <Flex gap={2} justifyContent="end">
                  {!isSaved && (
                    <Button
                      text={isSaving ? "Saving..." : "Save"}
                      color="blue"
                      size="md"
                      onClick={saveAllEditedContent}
                      disabled={isSaving}
                    />
                  )}
                  {isSaved && (
                    <Button text="Saved" color="green" size="md" disabled />
                  )}
                  <Button
                    text="Cancel"
                    color="red"
                    size="md"
                    onClick={handleCancelAllSections}
                    disabled={isSaving}
                  />
                </Flex>
              )}
            </Accordion>
          </Box>
        </Flex>
        <Box
          paddingY={6}
          paddingX={4}
          width="100%"
          borderStyle="shadow"
          rounding={4}
        >
          <Box marginBottom={4} rounding={4} paddingX={3}>
            <Text align="left" size="300" weight="bold">
              Chat History
            </Text>
          </Box>
          {feedback.chatHistory &&
            feedback.chatHistory.map((chat, index) => (
              <Box
                key={index}
                paddingY={2}
                paddingX={4}
                display="flex"
                direction="column"
                marginBottom={1}
              >
                <Text size="lg">
                  {chat.role === "assistant"
                    ? "Assistant:"
                    : `${feedback.candidateName}:`}
                </Text>
                <Text color="subtle" size="md" style={{ marginTop: "4px" }}>
                  {chat.content}
                </Text>
              </Box>
            ))}
        </Box>
      </Box>
    </Box>
  );
};

const AppWrapper = () => {
  const { isSignedIn } = useUser();
  return (
    <>
      <Header isAuthenticated={isSignedIn} />
      <Feedback />
    </>
  );
};

export default AppWrapper;
